import React, { useEffect, useMemo, useState } from 'react';
import { useReducedMotion } from 'motion/react';
import { useSearchParams } from 'react-router-dom';
import { getCurrentUser } from '../../services/api';
import { FeatureFlagProvider } from '../../contexts/FeatureFlagContext';
import AIChatMessageList from '../../components/AIChat/AIChatMessageList';
import { useVantiChat } from '../../components/AIChat/useVantiChat';
import VantiScopePicker from '../../components/AIChat/VantiScopePicker';
import VantiFollowUpChips from './VantiFollowUpChips';
import VantiWindowEmptyState from './VantiWindowEmptyState';
import VantiWindowHeader from './VantiWindowHeader';
import VantiWindowInput from './VantiWindowInput';
import VantiWindowSidebar from './VantiWindowSidebar';

function VantiWindowInner() {
  const reduceMotion = useReducedMotion();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const user = getCurrentUser();

  const chat = useVantiChat({ autoLoad: true });

  // Deep-link: ?thread=<id> opens that thread on mount
  useEffect(() => {
    const threadParam = searchParams.get('thread');
    if (threadParam && threadParam !== chat.threadId) {
      chat.handleSelectThread(threadParam);
      setSearchParams({}, { replace: true });
    }
  }, []); // run once on mount

  const handleNewChat = () => {
    chat.clearChat();
    chat.setMemoryPanelOpen(false);
  };

  // Derive last activity timestamp from the active thread record
  const lastActivityAt = useMemo(() => {
    const active = chat.threads.find((t: any) => t.id === chat.threadId);
    return active?.updatedAt ?? active?.createdAt ?? null;
  }, [chat.threads, chat.threadId]);

  const showEmptyState = chat.messages.length === 0 && !chat.loading;

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-dark-base">
      {/* Collapsible Sidebar */}
      <VantiWindowSidebar
        collapsed={sidebarCollapsed}
        onToggleCollapsed={() => setSidebarCollapsed((v) => !v)}
        threads={chat.threads}
        threadsLoading={chat.threadsLoading}
        activeThreadId={chat.threadId}
        agents={chat.agents}
        selectedAgentId={chat.selectedAgentId}
        onSelectThread={chat.handleSelectThread}
        onDeleteThread={chat.handleDeleteThread}
        onNewChat={handleNewChat}
        onSelectAgent={chat.handleAgentSelect}
        user={user ? { username: user.username, role: user.role } : undefined}
      />

      {/* Main chat area */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <VantiWindowHeader
          threadTitle={chat.activeThreadDisplayTitle}
          messagesCount={chat.messages.length}
          lastActivityAt={lastActivityAt}
          onClearChat={handleNewChat}
          actionMenuRef={chat.actionMenuRef}
          actionMenuOpen={chat.actionMenuOpen}
          onToggleActionMenu={() => {
            chat.setActionMenuOpen((v) => !v);
            chat.setMemoryPanelOpen(false);
          }}
          memoryPanelOpen={chat.memoryPanelOpen}
          onToggleMemory={() => {
            chat.setMemoryPanelOpen((v) => !v);
            chat.setActionMenuOpen(false);
          }}
          hasExportableMessages={chat.hasExportableMessages}
          onCopyConversation={chat.handleCopyConversation}
          onExportMarkdown={chat.handleExportMarkdown}
          onExportPdf={chat.handleExportPdf}
          selectedAgentId={chat.selectedAgentId}
          onAgentChange={chat.handleAgentChange}
          agents={chat.agents}
          loading={chat.loading}
          streamingMessageId={chat.streamingMessageId}
          chatMode={chat.chatMode}
          navigationContextEnabled={chat.navigationContextEnabled}
          scopeChip={chat.scopeChip}
          scopeOverride={chat.scopeOverride}
          onOpenScopePicker={() => chat.setPickerOpen(true)}
          onClearScope={chat.scopeOverride ? () => chat.setScopeOverride(null) : undefined}
          ragContextEnabled={chat.ragContextEnabled}
          indexHint={chat.indexHint}
        />

        {/* Empty state or message list */}
        {showEmptyState ? (
          <VantiWindowEmptyState
            username={user?.username}
            onSend={(text) => chat.send(text)}
          />
        ) : (
          <AIChatMessageList
            listRef={chat.listRef}
            threadsOpen={false}
            threads={[]}
            threadsLoading={false}
            onCloseThreads={() => {}}
            onSelectThread={chat.handleSelectThread}
            onDeleteThread={chat.handleDeleteThread}
            onCooldown={chat.onCooldown}
            cooldownRemainingMs={chat.cooldownRemainingMs}
            lastSendPayload={null}
            onRetryLast={chat.retryLast}
            loading={chat.loading}
            messages={chat.messages}
            disabledByOrg={chat.disabledByOrg}
            configured={chat.configured}
            streamingMessageId={chat.streamingMessageId}
            streamingStatus={chat.streamingStatus}
            feedbackState={chat.feedbackState}
            copiedResponseId={chat.copiedResponseId}
            reduceMotion={reduceMotion}
            onFeedback={chat.handleFeedback}
            onCopyResponse={chat.handleCopyResponse}
            onAnswer={chat.handleMessageAnswer}
            onScopeSelect={chat.handleMessageScopeSelect}
            onInlineScopeSelect={chat.handleMessageInlineScopeSelect}
            onNavigate={chat.handleMessageNavigate}
            onModeChange={chat.handleModeChange}
            onSetInput={chat.handleSetInputFromAction}
            onFollowUp={chat.handleFollowUp}
            onRetryFailed={chat.handleRetryFailed}
          />
        )}

        {/* Follow-up chips — shown after the last AI response */}
        {!showEmptyState && (
          <VantiFollowUpChips
            messages={chat.messages}
            loading={chat.loading}
            onSend={(text) => chat.sendOrQueue(text)}
          />
        )}

        {/* Smarter input bar */}
        <VantiWindowInput
          memoryPanelOpen={chat.memoryPanelOpen}
          onCloseMemory={() => chat.setMemoryPanelOpen(false)}
          input={chat.input}
          inputRef={chat.inputRef}
          onInputChange={(e) => chat.setInput(e.target.value)}
          onKeyDown={chat.handleKeyDown}
          onSend={() => chat.sendOrQueueWithFormat()}
          onStop={chat.handleStop}
          loading={chat.loading}
          streamingMessageId={chat.streamingMessageId}
          onCooldown={chat.onCooldown}
          chatMode={chat.chatMode}
          onModeChange={chat.handleModeChange}
          scopeChip={chat.scopeChip}
          onClearScope={chat.scopeOverride ? () => chat.setScopeOverride(null) : undefined}
          reduceMotion={reduceMotion}
          slashPickerOpen={chat.slashPickerOpen}
          slashQuery={chat.slashQuery}
          slashSelectedIndex={chat.slashSelectedIndex}
          onSlashSelect={chat.onSlashSelect}
          formatModifier={chat.formatModifier}
          onClearFormatModifier={chat.onClearFormatModifier}
        />
      </div>

      <VantiScopePicker
        open={chat.pickerOpen}
        onClose={() => chat.setPickerOpen(false)}
        onSelect={(item) => chat.pickScope(item)}
      />
    </div>
  );
}

export default function VantiWindow() {
  return (
    <FeatureFlagProvider>
      <VantiWindowInner />
    </FeatureFlagProvider>
  );
}
